import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { CalendarDays, CheckCircle2, Mail, Phone, RefreshCw, Smartphone, UserRound } from 'lucide-react';
import { PageHeader, SectionCard, StatusBadge } from '../components/WebPresentation';
import { apiFetch, ApiError } from '../lib/api';
import { useMe } from '../hooks/useMe';
import { staffSetupRecoveryMessage, type StaffSetupRecoveryStatus } from '../lib/staff';
import { buildStaffGoogleAuthorizationSummary, type StaffGoogleAuthorizationSummary } from '../lib/staff-google-authorization';
import { buildStaffPrivateCoachingShellSummary, type StaffPrivateCoachingShellSummary } from '../lib/staff-private-coaching';
import type { GoogleWorkspacePermissionStatus, StaffGoogleWorkspaceAuthorizationStartResponse } from '@oracord/shared';

type GoogleWorkspaceMatchStatus =
  | 'matched'
  | 'unverified-google-email'
  | 'personal-google-email-blocked'
  | 'admin-setup-needed'
  | 'email-already-linked'
  | 'google-account-required'
  | 'google-workspace-sign-in-disabled';

interface StaffMatchProfile {
  id: string;
  displayName: string;
  workEmail: string | null;
}

interface StaffMatchBusinessNumber {
  phoneNumber: string;
}

type GoogleWorkspacePermissions = GoogleWorkspacePermissionStatus;

interface StaffMatchSuccessResponse {
  status: 'matched';
  profile: StaffMatchProfile;
  businessNumbers: StaffMatchBusinessNumber[];
  googlePermissions: GoogleWorkspacePermissions;
}

interface MobileSessionResponse {
  status: 'paired';
  authToken: string;
  identity: string;
  sessionId: string;
  staffProfileId: string;
  organizationId: string;
  profile: StaffMatchProfile;
  businessNumbers: StaffMatchBusinessNumber[];
  googlePermissions?: GoogleWorkspacePermissions;
}

interface StaffMatchErrorBody {
  status?: GoogleWorkspaceMatchStatus;
  error?: string;
}

interface StaffMatchErrorState {
  status: GoogleWorkspaceMatchStatus | 'unknown';
  title: string;
  message: string;
  nextAction: string;
}

export default function StaffSetup() {
  const { loading: meLoading } = useMe();
  const [data, setData] = useState<StaffMatchSuccessResponse | null>(null);
  const [mobileSession, setMobileSession] = useState<MobileSessionResponse | null>(null);
  const [pairing, setPairing] = useState(false);
  const [pairingError, setPairingError] = useState<string | null>(null);
  const [authorizationCheck, setAuthorizationCheck] = useState<StaffGoogleWorkspaceAuthorizationStartResponse | null>(null);
  const [authorizationChecking, setAuthorizationChecking] = useState(false);
  const [authorizationError, setAuthorizationError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<StaffMatchErrorState | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch<StaffMatchSuccessResponse>('/api/auth/google-workspace/match', { method: 'POST' });
      setData(response);
      setMobileSession(null);
      setPairingError(null);
      setAuthorizationCheck(null);
      setAuthorizationError(null);
    } catch (err) {
      setData(null);
      if (err instanceof ApiError) {
        const body = err.body as StaffMatchErrorBody | null;
        const recovery = staffSetupRecoveryMessage((body?.status ?? 'unknown') as StaffSetupRecoveryStatus);
        setError({
          status: body?.status ?? 'unknown',
          title: recovery.title,
          message: recovery.message,
          nextAction: recovery.nextAction,
        });
      } else {
        const recovery = staffSetupRecoveryMessage('unknown');
        setError({ status: 'unknown', ...recovery });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (meLoading) return;
    void load();
  }, [load, meLoading]);


  const pairMobileSession = useCallback(async () => {
    setPairing(true);
    setPairingError(null);
    try {
      const response = await apiFetch<MobileSessionResponse>('/api/auth/google-workspace/mobile-session', {
        method: 'POST',
        body: { platform: 'mobile', label: 'Google mobile pairing' },
      });
      setMobileSession(response);
      setData({
        status: 'matched',
        profile: response.profile,
        businessNumbers: response.businessNumbers,
        googlePermissions: response.googlePermissions ?? data?.googlePermissions ?? disconnectedGooglePermissions(),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setPairingError(err.message || 'Could not prepare mobile access. Please try again.');
      } else {
        setPairingError('Could not prepare mobile access. Please try again.');
      }
    } finally {
      setPairing(false);
    }
  }, [data?.googlePermissions]);

  const checkGoogleAuthorization = useCallback(async () => {
    setAuthorizationChecking(true);
    setAuthorizationError(null);
    try {
      const response = await apiFetch<StaffGoogleWorkspaceAuthorizationStartResponse>('/api/auth/google-workspace/authorization/start', {
        method: 'POST',
      });
      setAuthorizationCheck(response);
      if (response.authorizationUrl && response.redirectAvailable) {
        window.location.assign(response.authorizationUrl);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setAuthorizationError(err.message || 'Google authorization readiness could not be checked. Refresh and try again.');
      } else {
        setAuthorizationError('Google authorization readiness could not be checked. Refresh and try again.');
      }
    } finally {
      setAuthorizationChecking(false);
    }
  }, []);

  const permissionSummary = data ? buildStaffGoogleAuthorizationSummary(data.googlePermissions) : null;
  const privateCoachingSummary = data
    ? buildStaffPrivateCoachingShellSummary({
        matched: true,
        profileDisplayName: data.profile.displayName,
        hasBusinessNumber: data.businessNumbers.length > 0,
        hasMobileSession: Boolean(mobileSession),
        googlePermissionState: data.googlePermissions.state,
      })
    : null;

  if (meLoading) {
    return (
      <div className="mx-auto max-w-4xl space-y-6">
        <SectionCard className="p-5 text-sm text-slate-600">Checking your workspace access…</SectionCard>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        eyebrow="Staff setup"
        title="Your Oracord access"
        description="A short status page for your Google account connection."
        action={
          <button
            type="button"
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        }
      />

      {loading && !data && !error && (
        <SectionCard className="p-5 text-sm text-slate-600">Checking your Google account match…</SectionCard>
      )}

      {error && !data && (
        <SectionCard className="p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <StatusBadge tone="amber">Setup needs attention</StatusBadge>
              <h2 className="mt-3 text-lg font-semibold text-slate-950">{error.title}</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{error.message}</p>
              <p className="mt-2 max-w-2xl text-sm font-medium leading-6 text-slate-700">Next: {error.nextAction}</p>
            </div>
            <Mail className="h-8 w-8 text-amber-400" />
          </div>
        </SectionCard>
      )}

      {data && (
        <>
          <SectionCard className="p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <StatusBadge tone="emerald">Matched</StatusBadge>
                <h2 className="mt-3 text-lg font-semibold text-slate-950">Your staff profile is connected.</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  This match comes from your verified Google email and your active profile.
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <Fact icon={<UserRound className="h-4 w-4 text-slate-500" />} label="Team profile">
                {data.profile.displayName}
              </Fact>
              <Fact icon={<Mail className="h-4 w-4 text-slate-500" />} label="Work email">
                {data.profile.workEmail ?? 'Not listed'}
              </Fact>
            </div>
          </SectionCard>

          {permissionSummary && (
            <GoogleAuthorizationCard
              summary={permissionSummary}
              readiness={authorizationCheck}
              checking={authorizationChecking}
              error={authorizationError}
              onPrimaryAction={checkGoogleAuthorization}
            />
          )}

          <SectionCard className="p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Mobile app access</div>
                <h2 className="mt-3 text-lg font-semibold text-slate-950">Prepare mobile access from this matched profile.</h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                  This uses your verified Google Workspace match to prepare staff app access for your team profile. It does not provision a native business line or call an external phone service.
                </p>
              </div>
              <Smartphone className="h-8 w-8 text-slate-400" />
            </div>

            <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-950">
                    {mobileSession ? 'Mobile access prepared' : 'Ready to prepare mobile access'}
                  </div>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    {mobileSession
                      ? 'Mobile app access is linked to your staff profile. Use the mobile app Google sign-in handoff to continue.'
                      : 'No business number input is needed; your assigned number remains read-only below.'}
                  </p>
                </div>
                <StatusBadge tone={mobileSession ? 'emerald' : 'slate'}>{mobileSession ? 'Paired' : 'Not paired'}</StatusBadge>
              </div>

              {mobileSession && (
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <Fact icon={<UserRound className="h-4 w-4 text-slate-500" />} label="Linked profile ID">
                    {mobileSession.staffProfileId}
                  </Fact>
                  <Fact icon={<Smartphone className="h-4 w-4 text-slate-500" />} label="Session ID">
                    {mobileSession.sessionId}
                  </Fact>
                </div>
              )}

              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => void pairMobileSession()}
                  disabled={pairing}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Smartphone className="h-4 w-4" />
                  {pairing ? 'Preparing mobile access…' : mobileSession ? 'Refresh mobile access' : 'Prepare mobile access'}
                </button>
                {pairingError && <p className="mt-3 text-sm leading-6 text-rose-600">{pairingError}</p>}
              </div>
            </div>
          </SectionCard>

          {privateCoachingSummary && (
            <PrivateCoachingShell summary={privateCoachingSummary} />
          )}

          <SectionCard className="p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Business number</div>
                {data.businessNumbers.length > 0 ? (
                  <div className="mt-3 space-y-2">
                    {data.businessNumbers.map(number => (
                      <div key={number.phoneNumber} className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                        <Phone className="h-4 w-4 text-emerald-600" />
                        {number.phoneNumber}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-sm leading-6 text-slate-600">No business number is assigned to your team profile yet.</p>
                )}
              </div>
              <StatusBadge tone={data.businessNumbers.length > 0 ? 'emerald' : 'amber'}>
                {data.businessNumbers.length > 0 ? 'Assigned' : 'Not assigned'}
              </StatusBadge>
            </div>
          </SectionCard>

          <SectionCard className="p-5">
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Next steps</div>
            <div className="mt-4 space-y-3">
              <Step done title="App access ready" description="You can use Oracord with this organization." />
              <Step
                done={data.businessNumbers.length > 0}
                title={data.businessNumbers.length > 0 ? 'Business number assigned' : 'Business number not assigned yet'}
                description={data.businessNumbers.length > 0 ? 'Your assigned number is shown above.' : 'An admin can assign a number when it is ready.'}
              />
              <Step
                done={Boolean(mobileSession)}
                icon={<Smartphone className="h-4 w-4" />}
                title={mobileSession ? 'Mobile access linked to your profile' : 'Mobile access can be prepared from Google match'}
                description={mobileSession ? 'Mobile access is linked to your staff profile; assigned numbers stay read-only.' : 'Use the mobile access card above when the staff app needs a fresh handoff.'}
              />
              <Step
                done={permissionSummary?.complete ?? false}
                icon={<CalendarDays className="h-4 w-4" />}
                title={permissionSummary?.complete ? 'Google email/calendar authorization recorded' : 'Google email/calendar authorization needs setup'}
                description={permissionSummary?.nextAction ?? 'An admin can connect Gmail and Calendar permission status when the integration is ready.'}
              />
            </div>
          </SectionCard>
        </>
      )}
    </div>
  );
}

function GoogleAuthorizationCard({
  summary,
  readiness,
  checking,
  error,
  onPrimaryAction,
}: {
  summary: StaffGoogleAuthorizationSummary;
  readiness: StaffGoogleWorkspaceAuthorizationStartResponse | null;
  checking: boolean;
  error: string | null;
  onPrimaryAction: () => void;
}) {
  return (
    <SectionCard className="p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Google work data authorization</div>
          <h2 className="mt-3 text-lg font-semibold text-slate-950">{summary.title}</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{summary.detail}</p>
        </div>
        <StatusBadge tone={summary.tone}>{summary.badge}</StatusBadge>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <Fact icon={<Mail className="h-4 w-4 text-slate-500" />} label="Gmail permission">
          {summary.gmailLabel}
        </Fact>
        <Fact icon={<CalendarDays className="h-4 w-4 text-slate-500" />} label="Calendar permission">
          {summary.calendarLabel}
        </Fact>
      </div>

      <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-950">Staff-controlled Gmail/Calendar context</div>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-600">{summary.nextAction}</p>
          </div>
          <button
            type="button"
            onClick={onPrimaryAction}
            disabled={!summary.primaryActionEnabled || checking}
            className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-500 shadow-sm disabled:cursor-not-allowed disabled:opacity-70"
          >
            {checking ? 'Checking readiness…' : summary.primaryActionLabel}
          </button>
        </div>
        {readiness && (
          <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-sm leading-6 text-slate-600">
            <div className="font-semibold text-slate-950">{readiness.title}</div>
            <p className="mt-1">{readiness.detail}</p>
            <p className="mt-1 font-medium text-slate-700">Next: {readiness.nextAction}</p>
          </div>
        )}
        {error && <p className="mt-3 text-sm leading-6 text-rose-600">{error}</p>}
        <p className="mt-3 text-xs leading-5 text-slate-500">{summary.privacyNote}</p>
      </div>
    </SectionCard>
  );
}

function PrivateCoachingShell({ summary }: { summary: StaffPrivateCoachingShellSummary }) {
  return (
    <SectionCard className="p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Private coaching</div>
          <h2 className="mt-3 text-lg font-semibold text-slate-950">{summary.title}</h2>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">{summary.detail}</p>
        </div>
        <StatusBadge tone={summary.status === 'matched' ? 'purple' : 'amber'}>
          {summary.status === 'matched' ? 'Staff-only' : 'Setup needed'}
        </StatusBadge>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {summary.cards.map(card => (
          <div key={card.id} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm font-semibold text-slate-950">{card.title}</div>
              <StatusBadge tone={card.tone} size="sm" className="shrink-0">
                {card.badge}
              </StatusBadge>
            </div>
            <p className="mt-3 text-sm leading-6 text-slate-600">{card.detail}</p>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function Fact({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-sm font-semibold text-slate-950">{children}</div>
    </div>
  );
}

function Step({
  done = false,
  icon,
  title,
  description,
}: {
  done?: boolean;
  icon?: ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-3 rounded-2xl border border-slate-200 bg-white p-4">
      <div className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full ${done ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
        {icon ?? <CheckCircle2 className="h-4 w-4" />}
      </div>
      <div>
        <div className="text-sm font-semibold text-slate-950">{title}</div>
        <p className="mt-1 text-sm leading-6 text-slate-600">{description}</p>
      </div>
    </div>
  );
}

function disconnectedGooglePermissions(): GoogleWorkspacePermissions {
  return {
    state: 'not_connected',
    gmail: false,
    calendar: false,
    connectedAt: null,
    updatedAt: null,
  };
}
